import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  MONTH_FULL_ES,
  MonthSelection,
  MonthSelectorBarProps,
  buildYearGroups,
  monthsBetween,
  todaySelection,
} from './MonthSelectorBar.Functions';
import './MonthSelectorBar.Styles.css';

export const MonthSelectorBar: React.FC<MonthSelectorBarProps> = ({
  selected,
  onChange,
}) => {
  const trackRef  = useRef<HTMLDivElement>(null);
  // Anchor for Shift+click range calculation (last plain/Ctrl click)
  const anchorRef = useRef<MonthSelection | null>(null);
  const isFirst   = useRef(true);

  const today      = new Date();
  const todayYear  = today.getFullYear();
  const todayMonth = today.getMonth() + 1;
  const isAlreadyOnToday =
    selected.length === 1 &&
    selected[0].year === todayYear &&
    selected[0].month === todayMonth;

  // ── Scroll a specific pill into the center of the track ─────────────
  const scrollToMonth = useCallback((target: MonthSelection, behavior: ScrollBehavior) => {
    if (!trackRef.current) return;
    const track = trackRef.current;
    const btn   = track.querySelector<HTMLButtonElement>(
      `[data-msb-year="${target.year}"][data-msb-month="${target.month}"]`
    );
    if (!btn) return;
    const tRect = track.getBoundingClientRect();
    const bRect = btn.getBoundingClientRect();
    const pos   =
      track.scrollLeft +
      (bRect.left - tRect.left) +
      bRect.width  / 2 -
      tRect.width  / 2;
    track.scrollTo({ left: pos, behavior });
  }, []);

  // scrollTarget drives the scroll effect; initialize with first selection
  const [scrollTarget, setScrollTarget] = useState<MonthSelection | null>(
    () => selected[0] ?? null
  );

  useEffect(() => {
    if (!scrollTarget) return;
    const behavior: ScrollBehavior = isFirst.current ? 'auto' : 'smooth';
    isFirst.current = false;
    const id = setTimeout(() => scrollToMonth(scrollTarget, behavior), 50);
    return () => clearTimeout(id);
  }, [scrollTarget, scrollToMonth]);

  // ── Click handler: plain / Ctrl(Meta) / Shift ────────────────────────
  const handleClick = useCallback(
    (year: number, month: number, e: React.MouseEvent) => {
      const clicked: MonthSelection = { year, month };

      if (e.shiftKey && anchorRef.current) {
        // Range — all months between anchor and clicked (inclusive)
        onChange(monthsBetween(anchorRef.current, clicked));
      } else if (e.ctrlKey || e.metaKey) {
        // Toggle individual month (Ctrl/Cmd)
        const exists = selected.some(s => s.year === year && s.month === month);
        const next   = exists
          ? selected.filter(s => !(s.year === year && s.month === month))
          : [...selected, clicked];
        onChange(next.length > 0 ? next : [clicked]); // never allow empty
        anchorRef.current = clicked;
      } else {
        // Plain click — single selection
        onChange([clicked]);
        anchorRef.current = clicked;
      }

      setScrollTarget(clicked);
    },
    [selected, onChange]
  );

  // Center view on anchor (or first selected)
  const pivot  = anchorRef.current ?? selected[0] ?? todaySelection();
  const groups = buildYearGroups(pivot);

  return (
    <div className="msb-root">
      <div className="msb-strip">
        <div className="msb-track" ref={trackRef}>
          <div className="msb-inner">

            {groups.map((group) => (
              <div key={group.year} className="msb-year-group">
                <div className="msb-year-label">{group.year}</div>

                <div className="msb-months-row">
                  {group.months.map((m) => {
                    const isSelected = selected.some(
                      s => s.year === group.year && s.month === m
                    );
                    const isToday = group.year === todayYear && m === todayMonth;
                    return (
                      <button
                        key={`${group.year}-${m}`}
                        data-msb-year={group.year}
                        data-msb-month={m}
                        className={[
                          'msb-month-btn',
                          isSelected ? 'msb-month-btn--selected' : '',
                          isToday    ? 'msb-month-btn--today'    : '',
                        ].filter(Boolean).join(' ')}
                        aria-pressed={isSelected}
                        aria-label={`${MONTH_FULL_ES[m - 1]} ${group.year}`}
                        onClick={(e) => handleClick(group.year, m, e)}
                      >
                        {MONTH_FULL_ES[m - 1]}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

          </div>
        </div>
      </div>

      <button
        className={`msb-today-btn${isAlreadyOnToday ? ' msb-today-btn--active' : ''}`}
        onClick={() => {
          const t = { year: todayYear, month: todayMonth };
          onChange([t]);
          anchorRef.current = t;
          setScrollTarget(t);
        }}
        title="Ir al mes actual"
      >
        Hoy
      </button>
    </div>
  );
};

export default MonthSelectorBar;
